import { useCallback, useEffect, useState } from "react";
import QRCode from "qrcode";

type UseQrCodeOptions = {
  width?: number;
  margin?: number;
};

export default function useQrCode(initialData?: string | null, options?: UseQrCodeOptions) {
  const [qrSrc, setQrSrc] = useState<string | null>(null);

  const generate = useCallback(
    async (data?: string | null) => {
      const value = String(data ?? "");
      if (!value) {
        setQrSrc(null);
        return null;
      }
      try {
        const dataUrl = await QRCode.toDataURL(value, {
          width: options?.width ?? 200,
          margin: options?.margin ?? 6,
        });
        setQrSrc(dataUrl);
        return dataUrl;
      } catch (err) {
        setQrSrc(null);
        throw err;
      }
    },
    [options?.width, options?.margin]
  );

  const print = useCallback(
    async (data?: string | null, title?: string) => {
      const value = data ?? qrSrc;
      if (!value) {
        // nothing to print
        return;
      }
      // if value looks like a data URL, use it directly, otherwise generate
      const dataUrl = value.startsWith("data:") ? value : await generate(String(data));
      const w = window.open("", "", "width=400,height=500");
      if (!w) return;
      w.document.write(`
        <html>
          <head>
            <title>${title ?? "QR Code"}</title>
            <style>body{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;margin:0;font-family:Arial,sans-serif}h2{margin:8px 0}</style>
          </head>
          <body>
            ${title ? `<h2>${title}</h2>` : ""}
            <img src="${dataUrl}" alt="QR" style="width:300px;height:300px;object-fit:contain" />
          </body>
        </html>
      `);
      w.document.close();
      setTimeout(() => w.print(), 300);
    },
    [generate, qrSrc]
  );

  useEffect(() => {
    if (initialData) generate(initialData).catch(() => {});
  }, [initialData, generate]);

  return {
    qrSrc,
    generate,
    print,
  } as const;
}
